import {useMemo} from 'react';
import {useFlagsStatus} from '@unleash/proxy-client-react';
import useExperiment from '../../../../hooks/useExperiment';
import useRawFinancialAccountsFlag from '../../../../hooks/useRawFinancialAccountsFlag';
import getOverrideParams from '../../../../utils/parseUrlParams';
import {
  EXPERIMENT_CONFIGS,
  EXPERIMENT_FINANCIAL_ACCOUNTS,
} from '../../../../constants/experiments';

const useFinancialAccountsExperiment = () => {
  const experimentName = EXPERIMENT_FINANCIAL_ACCOUNTS;

  const config = EXPERIMENT_CONFIGS[experimentName]?.[process.env.ENV];
  const {experimentId, variants} = config ?? {};

  const controlVariantId = variants?.control?.id;
  const variantId = variants?.variant?.id;

  const isFeatureFlagEnabled = useRawFinancialAccountsFlag();
  const {flagsReady: isFlagsReady} = useFlagsStatus();

  const override = getOverrideParams(experimentId);
  const hasOverride = typeof override === 'string';

  // !!experimentId keeps Flipper inert in environments without a configured id
  const shouldUseExperiment =
    !hasOverride && isFeatureFlagEnabled && !!experimentId;

  const {isLoading, isActive, variant: hookVariant} = useExperiment({
    name: experimentName,
    isEnabled: shouldUseExperiment,
  });

  const resolvedVariant = hasOverride ? override : hookVariant;
  const isConfiguredVariant =
    resolvedVariant === controlVariantId || resolvedVariant === variantId;

  const isExperimentLoading = useMemo(() => {
    if (hasOverride) return false;
    if (!experimentId) return false;
    if (!isFlagsReady) return true;
    if (!isFeatureFlagEnabled) return false;

    return isLoading;
  }, [
    hasOverride,
    experimentId,
    isFlagsReady,
    isFeatureFlagEnabled,
    isLoading,
  ]);

  const isInVariant = useMemo(() => {
    if (!variantId) return false;

    if (hasOverride) {
      return override?.toString().trim() === variantId.toString().trim();
    }

    if (!isActive || !isConfiguredVariant) return false;

    return resolvedVariant === variantId;
  }, [
    variantId,
    hasOverride,
    override,
    isActive,
    isConfiguredVariant,
    resolvedVariant,
  ]);

  const isInControl = useMemo(() => {
    if (!controlVariantId) return false;

    if (hasOverride) {
      return override?.toString().trim() === controlVariantId.toString().trim();
    }

    if (!isFeatureFlagEnabled) return true;
    if (!isActive || !isConfiguredVariant) return false;

    return resolvedVariant === controlVariantId;
  }, [
    controlVariantId,
    hasOverride,
    override,
    isFeatureFlagEnabled,
    isActive,
    isConfiguredVariant,
    resolvedVariant,
  ]);

  return useMemo(() => {
    return {
      experimentId,
      variant: resolvedVariant,
      isInVariant,
      isInControl,
      isExperimentLoading,
      isFeatureFlagEnabled,
      isFlagsReady,
      isAssigned: hasOverride
        ? isConfiguredVariant
        : isActive && isConfiguredVariant,
    };
  }, [
    experimentId,
    resolvedVariant,
    isInVariant,
    isInControl,
    isExperimentLoading,
    isFeatureFlagEnabled,
    isFlagsReady,
    hasOverride,
    isActive,
    isConfiguredVariant,
  ]);
};

export default useFinancialAccountsExperiment;
